import type { HsClippingStorage } from '../../engine-new/storages/clipping-storage.class';
import { HsDefaultMaterial } from '../../graphic-render-new/materials/default-material.class';
import type { HsMaterial } from '../../graphic-render-new/materials/material.class';
import { HsPointsMaterial } from '../../graphic-render-new/materials/points-material.class';
import { HsTextureMaterial } from '../../graphic-render-new/materials/texture-material.class';
import type { HsDefaultShader } from '../../graphic-render-new/shaders/classes/default-shader.class';
import { HsShaderData } from '../../graphic-render-new/shaders/classes/shader-data.class';
import { HsShaderObjectType } from '../../graphic-render-new/shaders/enums/shader-object-type.enum';
import type { HsSceneMaterialCache } from './scene-material-cache.class';

export interface HsSceneMaterialsOptions {
  gl: WebGL2RenderingContext;
  cache: HsSceneMaterialCache;
  /** Prefix of every cache key; changing it (for example with the scene look) starts new materials. */
  cachePrefix: string;
  /** Scene opacity, multiplied into material alpha. */
  opacity: number;
  /** Draws every material opaque, ignoring material alpha. */
  disableTransparentMaterials: boolean;
  clippingStorage: HsClippingStorage | null;
  /** Adds the scene's lighting and effects to mesh shaders. */
  configureMeshShader(shaderData: HsShaderData): HsShaderData;
}

/**
 * Materials of a BIM scene: the base mesh, line and point materials, and per-color variants of them.
 * Variants with the same color, alpha, texture and look are cached and share shader programs.
 */
export class HsSceneMaterials {
  /** Mesh material for objects without a material color. */
  public readonly mesh: HsMaterial;
  public readonly lines: HsMaterial;
  public readonly points: HsPointsMaterial<HsDefaultShader>;

  public constructor(private readonly options: HsSceneMaterialsOptions) {
    const { gl, cachePrefix, opacity } = options;
    this.mesh = options.cache.get(`${cachePrefix}|mesh:base`, () => {
      const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
      shaderData.addColor().addDepth().addRayIntersection();
      options.configureMeshShader(shaderData);
      this.finish(shaderData, opacity);
      const material: HsMaterial = new HsDefaultMaterial(gl, shaderData.makeShader());
      material.color.set(0.76, 0.76, 0.76);
      return material;
    });
    this.lines = options.cache.get(`${cachePrefix}|lines:base`, () => {
      const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
      shaderData.addColor().addDepth();
      this.finish(shaderData, opacity);
      const material: HsMaterial = new HsDefaultMaterial(gl, shaderData.makeShader());
      material.color.set(0.14, 0.24, 0.9);
      return material;
    });
    this.points = options.cache.get(`${cachePrefix}|points:base`, () => {
      const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
      shaderData.addColor().addPointDepth().addPoints();
      this.finish(shaderData, opacity);
      const material: HsPointsMaterial<HsDefaultShader> = new HsPointsMaterial<HsDefaultShader>(
        gl,
        shaderData.makeShader()
      );
      material.color.set(0.9, 0.48, 0.08);
      return material;
    });
  }

  /** A mesh material in `rgba`, textured when `texture` is given; the base material without a color. */
  public meshMaterial(rgba: readonly number[] | null, textureId?: string, texture?: WebGLTexture): HsMaterial {
    if (rgba === null) {
      return this.mesh;
    }
    const alpha: number = this.alpha(rgba);
    const colorKey: string = `${rgba[0]}|${rgba[1]}|${rgba[2]}|${alpha}`;
    return this.options.cache.get(
      `${this.options.cachePrefix}|mesh:color:${colorKey}|texture:${textureId ?? ''}`,
      () => {
        const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
        shaderData.addColor().addDepth().addRayIntersection();
        if (texture !== undefined) {
          shaderData.addTexture();
        }
        this.options.configureMeshShader(shaderData);
        this.finish(shaderData, alpha);
        const shared: { shader: HsDefaultShader; compile: boolean } = this.options.cache.share(shaderData.makeShader());
        const material: HsMaterial =
          texture === undefined
            ? new HsDefaultMaterial(this.options.gl, shared.shader, shared.compile)
            : new HsTextureMaterial(this.options.gl, shared.shader, shared.compile);
        material.color.set(rgba[0], rgba[1], rgba[2]);
        if (material instanceof HsTextureMaterial) {
          material.texture = texture ?? null;
        }
        return material;
      }
    );
  }

  /** Material of an instanced mesh collection in `rgba`, or in the base mesh color. */
  public collectionMaterial(rgba: readonly number[] | null): HsMaterial {
    const alpha: number = rgba === null ? 1 : this.alpha(rgba);
    const colorKey: string = `${rgba?.slice(0, 3).join('|') ?? this.mesh.color.values.join('|')}|${alpha}`;
    return this.options.cache.get(`${this.options.cachePrefix}|collection:${colorKey}`, () => {
      const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.collection);
      shaderData.addColor().addDepth().addRayIntersection();
      this.options.configureMeshShader(shaderData);
      this.finish(shaderData, alpha);
      const shared: { shader: HsDefaultShader; compile: boolean } = this.options.cache.share(shaderData.makeShader());
      const material: HsMaterial = new HsDefaultMaterial(this.options.gl, shared.shader, shared.compile);
      material.color.set(
        rgba?.[0] ?? this.mesh.color.r,
        rgba?.[1] ?? this.mesh.color.g,
        rgba?.[2] ?? this.mesh.color.b
      );
      return material;
    });
  }

  /** A line material in `rgba`; the base line material without a color. */
  public lineMaterial(rgba: readonly number[] | null): HsMaterial {
    if (rgba === null) {
      return this.lines;
    }
    const alpha: number = this.alpha(rgba);
    const colorKey: string = `${rgba[0]}|${rgba[1]}|${rgba[2]}|${alpha}`;
    return this.options.cache.get(`${this.options.cachePrefix}|lines:color:${colorKey}`, () => {
      const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
      shaderData.addColor().addDepth();
      this.finish(shaderData, alpha);
      const shared: { shader: HsDefaultShader; compile: boolean } = this.options.cache.share(shaderData.makeShader());
      const material: HsMaterial = new HsDefaultMaterial(this.options.gl, shared.shader, shared.compile);
      material.color.set(rgba[0], rgba[1], rgba[2]);
      return material;
    });
  }

  /**
   * Highlight materials of the selected node: yellow mesh, line and point materials drawn over the scene.
   * Without scene fresnel the mesh gets a fresnel rim (and normals in unlit shading) so it stands out.
   */
  public selectionMaterials(look: { fresnelEnabled: boolean; unlit: boolean }): {
    mesh: HsMaterial;
    lines: HsMaterial;
    points: HsPointsMaterial<HsDefaultShader>;
  } {
    const { gl, cache, cachePrefix } = this.options;
    const mesh: HsMaterial = cache.get(`${cachePrefix}|selected:mesh`, () => {
      const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
      shaderData.addColor().addDepth().addRayIntersection();
      this.options.configureMeshShader(shaderData);
      if (!look.fresnelEnabled) {
        if (look.unlit) {
          shaderData.addNormals();
        }
        shaderData.addFresnel(0.5);
      }
      this.finish(shaderData, 1);
      const material: HsMaterial = new HsDefaultMaterial(gl, shaderData.makeShader());
      material.color.set(1, 0.93, 0.25);
      return material;
    });
    const lines: HsMaterial = cache.get(`${cachePrefix}|selected:lines`, () => {
      const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
      shaderData.addColor().addDepth();
      this.finish(shaderData, 1);
      const material: HsMaterial = new HsDefaultMaterial(gl, shaderData.makeShader());
      material.color.set(1, 0.85, 0.2);
      return material;
    });
    const points: HsPointsMaterial<HsDefaultShader> = cache.get(`${cachePrefix}|selected:points`, () => {
      const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
      shaderData.addColor().addPointDepth().addPoints();
      this.finish(shaderData, 1);
      const material: HsPointsMaterial<HsDefaultShader> = new HsPointsMaterial(gl, shaderData.makeShader());
      material.color.set(1, 0.9, 0.35);
      return material;
    });
    return { mesh, lines, points };
  }

  private alpha(rgba: readonly number[]): number {
    return this.options.disableTransparentMaterials ? 1 : Math.max(0, Math.min(1, rgba[3] * this.options.opacity));
  }

  private finish(shaderData: HsShaderData, alpha: number): void {
    if (alpha < 1) {
      shaderData.addTransparency(alpha);
    }
    if (this.options.clippingStorage !== null) {
      // The storage only feeds the plane uniforms; without the fragment code nothing is cut.
      shaderData.setClippingStorage(this.options.clippingStorage).addClipping();
    }
  }
}
